import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Wrench, Waypoints, ShieldCheck, Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, AlertCircle } from "lucide-react";

const LOGO_SRC = "/brand/jmo-white.png";

const COPY = {
  pt: {
    badge: "PLATAFORMA DE CHAMADOS",
    headline: "Toda ordem de manutenção, do chamado à conclusão.",
    sub: "Registre chamados de serviço, acompanhe cada etapa e mantenha a equipe de campo da JMO Group conectada em um só lugar.",
    pillars: ["Abertura de chamados de serviço", "Rastreabilidade completa das ordens", "Equipe de campo conectada"],
    stat1: "Chamados a qualquer hora",
    stat2: "Ordens rastreáveis",
    welcome: "Bem-vindo de volta",
    welcomeSub: "Entre para acompanhar seus chamados.",
    email: "E-mail",
    password: "Senha",
    signIn: "Entrar",
    footer: "© 2026 JMO SYS Services · JMO Group Co.",
    tagline: "SERVICE · MAINTENANCE",
    invalid: "E-mail ou senha inválidos",
  },
  en: {
    badge: "SERVICE CALL PLATFORM",
    headline: "Every maintenance order, from call to completion.",
    sub: "Log service calls, track every step, and keep JMO Group's field team connected in one place.",
    pillars: ["Open service calls", "Full work-order traceability", "Connected field team"],
    stat1: "Calls anytime",
    stat2: "Traceable orders",
    welcome: "Welcome back",
    welcomeSub: "Sign in to track your calls.",
    email: "Email",
    password: "Password",
    signIn: "Sign in",
    footer: "© 2026 JMO SYS Services · JMO Group Co.",
    tagline: "SERVICE · MAINTENANCE",
    invalid: "Invalid email or password",
  },
};

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { lang, setLang } = useLang();
  const [showPw, setShowPw] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const t = COPY[lang] || COPY.en;
  const pillarIcons = [Wrench, Waypoints, ShieldCheck];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(t.invalid);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-between gap-12 px-8 md:px-[72px] py-10 bg-[#0B1F3A] font-['Inter'] overflow-hidden"
      style={{
        backgroundImage:
          "radial-gradient(900px 520px at 50% -8%, rgba(200,162,75,.12), transparent 55%)," +
          "radial-gradient(760px 620px at 8% 108%, rgba(52,104,176,.26), transparent 55%)," +
          "linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px)",
        backgroundSize: "auto, auto, 40px 40px, 40px 40px",
      }}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center px-8 md:px-[72px] py-7 z-10">
        <img src={LOGO_SRC} alt="JMO Group" className="h-[34px] w-auto opacity-95" data-testid="brand-logo" />
        <div className="inline-flex bg-white/[.06] border border-white/[.14] rounded-full p-[3px]" data-testid="lang-toggle">
          {["pt", "en"].map((code) => (
            <button
              key={code}
              onClick={() => setLang(code)}
              data-testid={`lang-${code}`}
              className={`px-3.5 py-[5px] rounded-full text-[12.5px] font-semibold uppercase transition-colors ${
                lang === code ? "bg-[#C8A24B] text-[#0B1F3A]" : "bg-transparent text-slate-300"
              }`}
            >
              {code}
            </button>
          ))}
        </div>
      </div>

      {/* Left brand column */}
      <div className="hidden lg:block flex-1 max-w-[500px] text-white">
        <div className="inline-flex items-center gap-2 bg-[#C8A24B]/[.14] border border-[#C8A24B]/30 text-[#E4C77E] px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-[.14em] mb-7">
          {t.badge}
        </div>
        <h2 className="font-['Poppins'] font-bold text-[42px] leading-[1.1] tracking-tight mb-[18px]">
          {t.headline}
        </h2>
        <p className="text-[15.5px] leading-[1.65] text-slate-300/90 mb-9 max-w-[430px]">{t.sub}</p>

        <div className="flex flex-col gap-5">
          {t.pillars.map((label, i) => {
            const Icon = pillarIcons[i];
            return (
              <div key={i} className="flex items-center gap-[15px]">
                <span className="inline-flex items-center justify-center w-[42px] h-[42px] rounded-[11px] bg-[#C8A24B]/[.14] text-[#C8A24B] shrink-0">
                  <Icon size={20} strokeWidth={1.8} />
                </span>
                <span className="text-[15px] text-slate-100">{label}</span>
              </div>
            );
          })}
        </div>

        <div className="flex gap-10 mt-10 pt-7 border-t border-white/10">
          <div>
            <div className="font-['Poppins'] font-extrabold text-[26px] text-white">24/7</div>
            <div className="text-[12.5px] text-slate-400 mt-0.5">{t.stat1}</div>
          </div>
          <div>
            <div className="font-['Poppins'] font-extrabold text-[26px] text-white">100%</div>
            <div className="text-[12.5px] text-slate-400 mt-0.5">{t.stat2}</div>
          </div>
        </div>
      </div>

      {/* Login card */}
      <div className="w-full max-w-[432px] shrink-0 bg-[#0B1F3A]/70 backdrop-blur-xl border border-white/10 rounded-[20px] p-10 md:p-[42px] shadow-[0_40px_80px_-30px_rgba(0,0,0,.6)] text-white">
        {/* Lockup */}
        <div className="inline-flex items-center gap-3 mb-7">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-[#C8A24B] to-[#B08F3E] text-[#0B1F3A] shadow-[0_8px_20px_-6px_rgba(200,162,75,.6)]">
            <Wrench size={22} strokeWidth={2} />
          </span>
          <div className="leading-tight">
            <div className="font-['Poppins'] font-extrabold text-[19px] tracking-[.12em]">JMO SYS</div>
            <div className="text-[9.5px] tracking-[.22em] text-slate-400 mt-[3px]">{t.tagline}</div>
          </div>
        </div>

        <h3 className="font-['Poppins'] font-bold text-[25px] tracking-tight mb-1.5">{t.welcome}</h3>
        <p className="text-[14px] text-slate-300/80 mb-7">{t.welcomeSub}</p>

        <form onSubmit={submit} data-testid="login-form">
          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 mb-4 rounded-[11px] bg-red-500/15 border border-red-500/30 px-3.5 py-2.5 text-[13px] text-red-200">
              <AlertCircle size={16} strokeWidth={2} className="shrink-0" />
              {error}
            </div>
          )}

          {/* Email */}
          <label className="block text-[11px] font-semibold tracking-[.06em] uppercase text-slate-400 mb-2">{t.email}</label>
          <div className="relative mb-[18px]">
            <Mail size={18} strokeWidth={1.7} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="input-email"
              placeholder="nome@jmogroup.com"
              className="w-full h-12 pl-11 pr-4 border border-white/[.14] rounded-[11px] bg-white/[.05] text-[14.5px] text-white placeholder:text-slate-500 focus:border-[#C8A24B] focus:ring-2 focus:ring-[#C8A24B]/20 outline-none transition"
            />
          </div>

          {/* Password */}
          <label className="block text-[11px] font-semibold tracking-[.06em] uppercase text-slate-400 mb-2">{t.password}</label>
          <div className="relative mb-6">
            <Lock size={18} strokeWidth={1.7} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="input-password"
              placeholder="••••••••"
              className="w-full h-12 pl-11 pr-12 border border-white/[.14] rounded-[11px] bg-white/[.05] text-[14.5px] text-white placeholder:text-slate-500 focus:border-[#C8A24B] focus:ring-2 focus:ring-[#C8A24B]/20 outline-none transition"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="toggle-password"
              aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-[34px] h-[34px] flex items-center justify-center text-slate-500 hover:text-slate-300 rounded-lg"
            >
              {showPw ? <EyeOff size={19} strokeWidth={1.7} /> : <Eye size={19} strokeWidth={1.7} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="btn-signin"
            className="w-full h-[52px] rounded-[11px] bg-gradient-to-br from-[#C8A24B] to-[#B08F3E] text-[#0B1F3A] font-['Poppins'] font-bold text-[15.5px] tracking-wide flex items-center justify-center gap-2 shadow-[0_12px_26px_-10px_rgba(200,162,75,.6)] hover:-translate-y-px transition-transform disabled:opacity-70 disabled:hover:translate-y-0"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <>{t.signIn}<ArrowRight size={18} strokeWidth={2.2} /></>}
          </button>
        </form>
      </div>

      {/* Footer */}
      <div className="absolute bottom-[22px] left-0 right-0 text-center text-[11.5px] text-slate-500">{t.footer}</div>
    </div>
  );
}
